import React, { useMemo } from 'react';

import { Box } from '@tanium/react-box';

import { ActiveCondition } from '../../utils/ActiveCondition';
import { ColorScale } from '../../utils/ColorScale';
import {
  DataKeyGetterProp,
  DataKeyType,
  LabelGetterProp,
  OptionalDataKeyGetterProp,
  OptionalLabelGetterProp,
} from '../../utils/DataProps';
import { defaultDataKeyGetter, defaultLabelGetter } from '../../utils/defaultGetters';
import { ConditionalBadge } from '../ConditionalBadge';

/**
 * Props to enable specifying an active condition.
 */
export interface ActiveConditionProps<TDatum> {
  /**
   * An active condition that should be indicated.
   */
  // TODO: support multiple conditions, including a primary condition for the badge
  activeCondition?: ActiveCondition<TDatum>;
}

type ActiveConditionBadgeProps<TDatum, TDataKeyType extends DataKeyType> = {
  activeCondition: ActiveCondition<TDatum>;
  colorScale?: ColorScale<TDataKeyType>;
  data?: readonly Readonly<TDatum>[];
} & DataKeyGetterProp<TDatum, TDataKeyType> &
  LabelGetterProp<TDatum>;

const ActiveConditionBadge = <TDatum, TDataKeyType extends DataKeyType>({
  activeCondition,
  colorScale,
  data,
  dataKeyGetter,
  labelGetter,
}: ActiveConditionBadgeProps<TDatum, TDataKeyType>): JSX.Element => {
  // This is an abuse of useMemo to do something that's really an effect, but we need to
  // pre-initialize the color scale _before_ rendering the conditional badge, which can't be done
  // with an effect. This way the item(s) in the badge don't always claim the first implicit color
  // assignments.
  useMemo(() => {
    if (colorScale && data) {
      data.forEach((d) => {
        colorScale.get(dataKeyGetter(d));
      });
    }
  }, [colorScale, data, dataKeyGetter]);

  const badgeItemLabel = activeCondition ? labelGetter(activeCondition.item) : undefined;
  const badgeItemColor = activeCondition ? colorScale?.get(dataKeyGetter(activeCondition.item)) : undefined;

  return (
    <Box display="flex" justifyContent="flex-end">
      <ConditionalBadge
        badgeLabel={activeCondition.conditionLabel}
        itemLabel={badgeItemLabel}
        itemColor={badgeItemColor}
        threshold={activeCondition.threshold}
        type={activeCondition.type}
      />
    </Box>
  );
};

type Maybe<T> = T | Partial<Record<keyof T, never>>;

type ActiveConditionBadgeRendererProps<TDatum, TDataKeyType extends DataKeyType> = {
  activeCondition?: ActiveCondition<TDatum>;
  colorScale?: ColorScale<TDataKeyType>;
} & Maybe<
  {
    data: readonly Readonly<TDatum>[];
  } & OptionalDataKeyGetterProp<TDatum, TDataKeyType> &
    OptionalLabelGetterProp<TDatum>
>;

export const activeConditionBadgeRenderer = <TDatum, TDataKeyType extends DataKeyType>({
  activeCondition,
  dataKeyGetter = defaultDataKeyGetter,
  labelGetter = defaultLabelGetter,
  ...props
}: ActiveConditionBadgeRendererProps<TDatum, TDataKeyType>): JSX.Element | null => {
  if (!activeCondition) {
    return null;
  }

  return (
    <ActiveConditionBadge
      activeCondition={activeCondition}
      dataKeyGetter={dataKeyGetter}
      labelGetter={labelGetter}
      {...props}
    />
  );
};
